// Report card component

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Heading,
  Text,
  Button,
  HStack,
  IconButton,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiEye, FiShare2, FiEdit, FiTrash } from 'react-icons/fi';
import { Report } from '@/lib/sdk/reports/types';
interface ReportCardProps {
  report: Report;
  onView: (report: Report) => void;
  onShare: (report: Report) => void;
  onEdit?: (report: Report) => void;
  onDelete?: (report: Report) => void;
  canShare?: boolean;
  isSuperAdmin?: boolean;
}

/**
 * Report card component displaying report information
 */
export function ReportCard({
  report,
  onView,
  onShare,
  onEdit,
  onDelete,
  canShare = false,
  isSuperAdmin = false,
}: ReportCardProps) {
  const cardGradient = useColorModeValue(
      'linear(to-br, white, brand.50)',
      'linear(to-br, gray.800, gray.700)'
  );
  return (
    <Card
      size="md"
      variant="outline"
      _hover={{ shadow: 'md', borderColor: 'border-brand-light' }}
      transition="all 0.2s"
      bg="bg-surface"
      bgGradient={cardGradient}
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="md">{report.title}</Heading>
          {isSuperAdmin && (
            <HStack spacing={1}>
              {onEdit && (
                <Tooltip label="Edit report">
                  <IconButton
                    aria-label="Edit report"
                    icon={<FiEdit />}
                    size="sm"
                    variant="ghost"
                    colorScheme="brand"
                    onClick={() => onEdit(report)}
                  />
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip label="Delete report">
                  <IconButton
                    aria-label="Delete report"
                    icon={<FiTrash />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => onDelete(report)}
                  />
                </Tooltip>
              )}
            </HStack>
          )}
        </HStack>
      </CardHeader>

      <CardBody pt={2} pb={2}>
        <Text color="gray.400" fontSize="sm" noOfLines={3}>
          {report.description}
        </Text>
      </CardBody>

      <CardFooter pt={2}>
        <HStack spacing={2} w="full">
          <Button
            leftIcon={<FiEye />}
            colorScheme="brand"
            size="sm"
            flex={1}
            onClick={() => onView(report)}
          >
            View Dashboard
          </Button>
          {canShare && (
            <Button
              leftIcon={<FiShare2 />}
              variant="outline"
              colorScheme="brand"
              size="sm"
              onClick={() => onShare(report)}
            >
              Share
            </Button>
          )}
        </HStack>
      </CardFooter>
    </Card>
  );
}
